import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { env } from '../../../shared/config/env'
import { fetchPosts } from '../api/postsClient'
import { type PostsQuery, type Post } from '../model/contracts'
import { dedupePostsByKey, sortPostsByTimestampDesc, toPost } from '../utils/postsUtils'

export type PostsFeedViewModel = {
  posts: Post[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: unknown
}

type PostsPage = {
  items: Post[]
  total?: number
}

export function usePostsFeed(postsQuery: PostsQuery) {
  const limit = postsQuery.limit ?? env.VITE_POSTS_PAGE_SIZE
  const initialPage = postsQuery.page ?? 1
  const queryKey = ['posts', { ...postsQuery, limit, page: initialPage }] as const

  const result = useInfiniteQuery<
    PostsPage,
    Error,
    InfiniteData<PostsPage, number>,
    typeof queryKey,
    number
  >({
    queryKey,
    initialPageParam: initialPage,
    queryFn: async ({ pageParam, signal }) => {
      const res = await fetchPosts({ ...postsQuery, limit, page: pageParam }, signal)
      return { items: res.items.map(toPost), total: res.total }
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.items.length === 0) return undefined

      const total = allPages.find((p) => typeof p.total === 'number')?.total
      if (typeof total === 'number') {
        const loaded = allPages.reduce((acc, p) => acc + p.items.length, 0)
        if (loaded >= total) return undefined
      }

      if (lastPage.items.length < limit) return undefined
      return initialPage + allPages.length
    },
  })

  const posts = useMemo(() => {
    const all = (result.data?.pages ?? []).flatMap((p) => p.items)
    return sortPostsByTimestampDesc(dedupePostsByKey(all))
  }, [result.data])

  const viewModel: PostsFeedViewModel = {
    posts,
    isLoading: result.isLoading,
    isFetchingNextPage: result.isFetchingNextPage,
    hasNextPage: Boolean(result.hasNextPage),
    error: result.error,
  }

  return {
    viewModel,
    fetchNextPage: result.fetchNextPage,
    refetch: result.refetch,
  }
}
